(function () {
  const fallbackPolicy = {
    storageKey: "cookie_consent",
    cookieExpiryDays: 365,
    policyVersion: "1.0",
    bannerMessage: "This website uses cookies to improve your experience.",
    acceptText: "ACCEPT ALL",
    rejectText: "REJECT ALL",
    readMoreText: "CUSTOMIZE",
    modalTitle: "Cookie Preferences",
    modalDescription: "We use cookies to enhance your browsing experience.",
    saveText: "SAVE PREFERENCES",
    alwaysActiveText: "Always Active",
    cancelText: "Cancel",
    categories: [
      {
        id: "essential",
        title: "Essential Cookies",
        description: "These cookies are necessary for the website to function.",
        always_active: true,
      },
      {
        id: "analytics",
        title: "Analytics Cookies",
        description: "These cookies help us understand how visitors interact with this website.",
        always_active: false,
      },
      {
        id: "marketing",
        title: "Marketing Cookies",
        description: "These cookies are used to make advertising messages more relevant.",
        always_active: false,
      },
      {
        id: "personalisation",
        title: "Personalization Cookies",
        description: "These cookies allow the website to remember choices you make.",
        always_active: false,
      },
    ],
  };

  function readConfig() {
    const element = document.getElementById("cookie-consent-config");
    if (!element) return {};
    try {
      return JSON.parse(element.textContent || "{}");
    } catch (error) {
      return {};
    }
  }

  function storage() {
    return window.siteStorage || window.localStorage;
  }

  function getStorage(key) {
    try {
      return storage().get ? storage().get(key) : storage().getItem(key);
    } catch (error) {
      return null;
    }
  }

  function setStorage(key, value) {
    try {
      if (storage().set) storage().set(key, value);
      else storage().setItem(key, value);
      return true;
    } catch (error) {
      return false;
    }
  }

  function removeStorage(key) {
    try {
      if (storage().remove) storage().remove(key);
      else storage().removeItem(key);
      return true;
    } catch (error) {
      return false;
    }
  }

  function text(value, fallback = "") {
    if (value && typeof value === "object") {
      const lang = (window.__docLang || document.documentElement.lang || "en").split(/[-_]/)[0];
      return value[window.__docLang] || value[lang] || value.en || Object.values(value).find(Boolean) || fallback;
    }
    return value || fallback;
  }

  function escapeHtml(value) {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function normalizeCategory(category) {
    return {
      id: category.id,
      title: text(category.title),
      description: text(category.description),
      always_active: category.always_active === true || category.alwaysActive === true,
      enabled: category.enabled !== false && category.enabled !== 0,
    };
  }

  function normalizePolicyResponse(response, current) {
    const policy = response?.data?.policy_content || response?.data || response?.policy_content || {};
    return {
      ...current,
      storageKey: policy.storage_key || current.storageKey,
      bannerMessage: text(policy.banner?.message || policy.bannerMessage, current.bannerMessage),
      acceptText: text(policy.banner?.accept_text || policy.acceptText, current.acceptText),
      rejectText: text(policy.banner?.reject_text || policy.rejectText, current.rejectText),
      readMoreText: text(policy.banner?.read_more_text || policy.readMoreText, current.readMoreText),
      modalTitle: text(policy.modal?.title || policy.modalTitle || policy.preferences?.title, current.modalTitle),
      modalDescription: text(policy.modal?.description || policy.modalDescription || policy.preferences?.description, current.modalDescription),
      saveText: text(policy.modal?.save_text || policy.saveText || policy.preferences?.save_text, current.saveText),
      cookieExpiryDays: policy.cookie_expiry_days || policy.cookieExpiryDays || current.cookieExpiryDays,
      policyVersion: policy.version || response?.data?.version || policy.policyVersion || current.policyVersion,
      categories: (policy.categories || policy.preferences?.categories || current.categories)
        .map(normalizeCategory)
        .filter((category) => category.enabled),
    };
  }

  class CookieConsent {
    constructor() {
      const config = readConfig();
      this.policy = {
        ...fallbackPolicy,
        ...config,
        categories: fallbackPolicy.categories.map(normalizeCategory),
      };
      this.loadPolicyOnInit = config.loadPolicyOnInit !== false;
      this.categories = this.makePreferences(false);
      this.root = this.ensureRoot();
      this.banner = this.root.querySelector("[data-cookie-banner]");
      this.modal = this.root.querySelector("[data-cookie-modal]");
      this.dialog = this.root.querySelector("[data-cc-dialog]");
      this.categoryContainer = this.root.querySelector("[data-cc-categories]");
    }

    ensureRoot() {
      let root = document.querySelector("[data-cookie-consent-root]");
      if (!root) {
        root = document.createElement("div");
        root.dataset.cookieConsentRoot = "";
        root.hidden = true;
        document.body.appendChild(root);
      }

      root.innerHTML = `
        <div aria-label="Cookie consent" class="cc-banner" data-cookie-banner hidden role="banner">
          <div class="cc-banner-inner">
            <p class="t-body w-r text-text-primary cc-banner-text" data-cc-banner-message></p>
            <div class="cc-banner-actions-desktop">
              <button class="cc-btn cc-btn-outline" data-cc-open type="button"></button>
              <button class="cc-btn cc-btn-primary" data-cc-reject type="button"></button>
              <button class="cc-btn cc-btn-primary" data-cc-accept type="button"></button>
            </div>
            <div class="cc-banner-actions-mobile">
              <button class="cc-btn cc-btn-primary" data-cc-accept type="button"></button>
              <div>
                <button class="cc-btn cc-btn-primary" data-cc-reject type="button"></button>
                <button class="cc-btn cc-btn-outline" data-cc-open type="button"></button>
              </div>
            </div>
          </div>
        </div>
        <div class="cc-modal-overlay" data-cookie-modal hidden>
          <div aria-hidden="true" class="cc-modal-backdrop" data-cc-close></div>
          <div aria-labelledby="cookie-preferences-title" aria-modal="true" class="cc-modal" data-cc-dialog role="dialog" tabindex="-1">
            <div class="cc-modal-header">
              <div><h2 class="t-body w-r cc-modal-title" data-cc-modal-title id="cookie-preferences-title"></h2></div>
              <button aria-label="Close preferences" class="cc-modal-close" data-cc-close type="button">
                <span class="icon mc-icon-body" aria-hidden="true"><i class="ci ci-xmark" aria-hidden="true"></i></span>
              </button>
            </div>
            <p class="t-body w-r text-text-primary cc-modal-desc" data-cc-modal-description></p>
            <div data-cc-categories></div>
            <div class="cc-modal-footer">
              <div class="cc-modal-footer-actions">
                <button aria-label="Cancel and close" class="cc-btn cc-btn-ghost" data-cc-close type="button"><span data-cc-cancel-text></span></button>
                <button aria-label="Save preferences and close" class="cc-btn cc-btn-primary" data-cc-save type="button"></button>
              </div>
            </div>
          </div>
        </div>
      `;
      root.hidden = false;
      return root;
    }

    init() {
      this.root.addEventListener("click", (event) => {
        if (event.target.closest("[data-cc-open]")) this.openModal();
        if (event.target.closest("[data-cc-accept]")) this.acceptAll();
        if (event.target.closest("[data-cc-reject]")) this.rejectAll();
        if (event.target.closest("[data-cc-close]") || event.target === this.modal) this.closeModal();
        const toggle = event.target.closest("[data-cc-toggle]");
        if (toggle) this.toggleCategory(toggle.dataset.ccToggle);
        if (event.target.closest("[data-cc-save]")) this.savePreferences();
      });
      document.addEventListener("click", (event) => {
        if (event.target.closest("[data-open-cookie-preferences], .fsu-cookie-btn")) this.openModal();
      });
      window.addEventListener("keydown", (event) => {
        if (event.key === "Escape" && !this.modal.hidden) this.closeModal();
      });
      window.addEventListener("open-cookie-preferences", () => this.openModal());

      this.render();
      this.checkConsent();
      if (this.loadPolicyOnInit) this.loadPolicyFromServer();
    }

    makePreferences(value) {
      return this.policy.categories.reduce((preferences, category) => {
        preferences[category.id] = category.always_active ? true : value;
        return preferences;
      }, {});
    }

    setText(selector, value) {
      this.root.querySelectorAll(selector).forEach((element) => {
        element.textContent = value || "";
        if (element.tagName === "BUTTON") element.setAttribute("aria-label", value || "");
      });
    }

    render() {
      this.setText("[data-cc-banner-message]", this.policy.bannerMessage);
      this.setText("[data-cc-open]", this.policy.readMoreText);
      this.setText("[data-cc-reject]", this.policy.rejectText);
      this.setText("[data-cc-accept]", this.policy.acceptText);
      this.setText("[data-cc-modal-title]", this.policy.modalTitle);
      this.setText("[data-cc-save]", this.policy.saveText);
      this.setText("[data-cc-cancel-text]", this.policy.cancelText);
      this.root.querySelector("[data-cc-modal-description]").innerHTML = this.policy.modalDescription || "";
      this.renderCategories();
    }

    renderCategories() {
      this.categoryContainer.innerHTML = this.policy.categories.map((category) => `
        <div class="cc-category" data-cc-category="${escapeHtml(category.id)}">
          <div class="cc-category-row">
            <div class="cc-category-info">
              <h3 class="t-base w-r cc-category-title">${escapeHtml(category.title)}</h3>
              <p class="t-body w-r text-text-primary cc-category-desc">${escapeHtml(category.description)}</p>
            </div>
            <div class="cc-category-control">
              ${category.always_active ? this.renderAlwaysActive() : this.renderToggle(category)}
            </div>
          </div>
        </div>
      `).join("");
      this.updateToggles();
    }

    renderAlwaysActive() {
      return `<div class="cc-badge-active">${escapeHtml(this.policy.alwaysActiveText)}</div>`;
    }

    renderToggle(category) {
      return `
        <button type="button" class="cc-toggle" role="switch" data-cc-toggle="${escapeHtml(category.id)}" aria-checked="false">
          <span class="sr-only">Enable ${escapeHtml(category.title)}</span>
          <span aria-hidden="true" class="cc-toggle-thumb"></span>
        </button>
      `;
    }

    updateToggles() {
      this.categoryContainer.querySelectorAll("[data-cc-toggle]").forEach((button) => {
        const checked = !!this.categories[button.dataset.ccToggle];
        button.classList.toggle("cc-toggle--on", checked);
        button.setAttribute("aria-checked", String(checked));
        button.querySelector(".cc-toggle-thumb")?.classList.toggle("cc-thumb--on", checked);
      });
    }

    checkConsent() {
      const stored = getStorage(this.policy.storageKey);
      if (stored) {
        try {
          const storedData = JSON.parse(stored);
          const expires = new Date(storedData.timestamp);
          expires.setDate(expires.getDate() + Number(this.policy.cookieExpiryDays || 365));
          if (new Date() < expires) {
            this.categories = { ...this.categories, ...storedData.preferences };
            this.banner.hidden = true;
            this.applyCookieSettings();
            this.updateToggles();
            return;
          }
        } catch (error) {
          removeStorage(this.policy.storageKey);
        }
      }
      this.banner.hidden = false;
    }

    async loadPolicyFromServer() {
      try {
        const response = await fetch(window.location.href, {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
          body: JSON.stringify({ workflows: ["get_cookie_policy"], jinja_data: {} }),
        });
        if (!response.ok || !(response.headers.get("content-type") || "").includes("application/json")) return;
        const data = await response.json();
        if (data.success === 0) return;
        const previousStorageKey = this.policy.storageKey;
        this.policy = normalizePolicyResponse(data, this.policy);
        this.categories = { ...this.makePreferences(false), ...this.categories };
        this.render();
        if (previousStorageKey !== this.policy.storageKey) this.checkConsent();
      } catch (error) {
        return false;
      }
      return true;
    }

    toggleCategory(categoryId) {
      const category = this.policy.categories.find((item) => item.id === categoryId);
      if (!category || category.always_active) return;
      this.categories[categoryId] = !this.categories[categoryId];
      this.updateToggles();
    }

    openModal() {
      this.modal.hidden = false;
      document.body.style.overflow = "hidden";
      this.dialog?.focus({ preventScroll: true });
    }

    closeModal() {
      this.modal.hidden = true;
      document.body.style.overflow = "";
    }

    acceptAll() {
      this.categories = this.makePreferences(true);
      this.saveAndClose();
    }

    rejectAll() {
      this.categories = this.makePreferences(false);
      this.saveAndClose();
    }

    savePreferences() {
      this.saveAndClose();
      this.closeModal();
    }

    saveAndClose() {
      setStorage(this.policy.storageKey, JSON.stringify({
        preferences: this.categories,
        timestamp: new Date().toISOString(),
      }));
      this.applyCookieSettings();
      this.banner.hidden = true;
      this.saveToServer();
    }

    async saveToServer() {
      window.cookieConsentData = {
        preferences: this.categories,
        policy_version: this.policy.policyVersion,
        timestamp: new Date().toISOString(),
      };

      try {
        await fetch(window.location.href, {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
          body: JSON.stringify({
            workflows: ["save_consent_preferences"],
            jinja_data: {
              preferences: this.categories,
              policy_version: this.policy.policyVersion,
              user_agent: navigator.userAgent,
              timestamp: new Date().toISOString(),
            },
          }),
        });
      } catch (error) {
        return false;
      }
      return true;
    }

    applyCookieSettings() {
      this.setConsentCookie("analytics_consent", this.categories.analytics);
      this.setConsentCookie("marketing_consent", this.categories.marketing);
      this.setConsentCookie("personalisation_consent", this.categories.personalisation);
      this.setCookie("cookie_consent_set", "true", this.policy.cookieExpiryDays);
      window.dispatchEvent(new CustomEvent("cookieConsentUpdate", {
        detail: { categories: this.categories, timestamp: new Date().toISOString() },
      }));
    }

    setConsentCookie(name, enabled) {
      if (enabled) this.setCookie(name, "true", this.policy.cookieExpiryDays);
      else this.deleteCookie(name);
    }

    setCookie(name, value, days) {
      const expires = new Date();
      expires.setTime(expires.getTime() + Number(days || 365) * 24 * 60 * 60 * 1000);
      document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
    }

    deleteCookie(name) {
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/`;
    }
  }

  function initCookieConsent() {
    window.cookieConsent = new CookieConsent();
    window.cookieConsent.init();
  }

  window.openCookiePreferences = () => window.dispatchEvent(new CustomEvent("open-cookie-preferences"));
  window.initCookieConsent = initCookieConsent;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initCookieConsent);
  } else {
    initCookieConsent();
  }
})();
